import { deleteData, getData, patchData, postData } from "../api/api";
import type {
  DashboardExecuteResponse,
  PageResponse,
  ReportDashboard,
  VizCatalogResponse,
} from "../types/dashboard";
import type { DashboardVisualPlacement } from "../types/dashboard";
import type { WidgetExecuteRequest } from "../types/widget";
import type { CreateShareLinkRequest, ShareLink } from "../types/share";

export type DrillDownResponse = {
  field: string;
  value: string;
  index: string;
  columns: string[];
  rows: Array<Record<string, unknown>>;
  totalRows: number;
};

const BASE = "/dashboards";

export const dashboardService = {
  list: (page = 0, size = 20) =>
    getData(`${BASE}?page=${page}&size=${size}`) as Promise<PageResponse<ReportDashboard>>,

  get: (id: string) => getData(`${BASE}/${id}`) as Promise<ReportDashboard>,

  create: (dashboard: ReportDashboard) => postData(BASE, dashboard) as Promise<ReportDashboard>,

  update: (id: string, dashboard: ReportDashboard) =>
    patchData(`${BASE}/${id}`, dashboard) as Promise<ReportDashboard>,

  remove: (id: string) => deleteData(`${BASE}/${id}`),

  execute: (id: string, body: WidgetExecuteRequest) =>
    postData(`${BASE}/${id}/execute`, body) as Promise<DashboardExecuteResponse>,

  vizCatalog: () => getData(`${BASE}/viz-catalog`) as Promise<VizCatalogResponse>,

  previewVisual: (id: string, visual: DashboardVisualPlacement, execute?: WidgetExecuteRequest) =>
    postData(`${BASE}/${id}/visuals/preview`, { visual, execute }) as Promise<
      NonNullable<DashboardExecuteResponse["visuals"]>[number]
    >,

  drillDown: (
    id: string,
    body: {
      field: string;
      value: string;
      from: string;
      to: string;
      index?: string;
      limit?: number;
    }
  ) => postData(`${BASE}/${id}/drill-down`, body) as Promise<DrillDownResponse>,

  createShareLink: (id: string, body: CreateShareLinkRequest) =>
    postData(`${BASE}/${id}/share`, body) as Promise<ShareLink>,

  listShareLinks: (id: string) => getData(`${BASE}/${id}/share`) as Promise<ShareLink[]>,

  revokeShareLink: (token: string) => deleteData(`/share-links/${token}`),

  addWidget: async (dashboardId: string, widgetId: string) => {
    const d = (await getData(`${BASE}/${dashboardId}`)) as ReportDashboard;
    const placements = d.layout?.widgets || [];
    return patchData(`${BASE}/${dashboardId}`, {
      ...d,
      layout: {
        ...d.layout,
        widgets: [
          ...placements,
          { widgetId, x: 0, y: Infinity, w: 6, h: 4, minW: 3, minH: 2 },
        ],
      },
    }) as Promise<ReportDashboard>;
  },
};
